import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { InvoiceDto, InvoiceStatus, IssuerProfileDto, QuoteItemKind } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon } from '../icons';
import { euro, euroInput, parseEuro } from '../format';
import { Empty, ErrorBanner, Field, Modal, Panel, Pill } from '../ui';
import { DateInput } from '../pickers';
import { ask } from '../dialogs';
import { SchedulePicker, newWhen, whenError, whenLabel, whenToIso, type When } from '../schedule';

const STATUS: Record<InvoiceStatus, { label: string; tone: 'good' | 'warn' | 'bad' | 'info' | 'soft'; hint: string }> = {
  draft: { label: 'Bozza', tone: 'soft', hint: 'Non ancora emesse: si possono cambiare' },
  issued: { label: 'Emessa', tone: 'warn', hint: 'Emesse: manca l’invio della fattura elettronica' },
  sent: { label: 'Inviata', tone: 'info', hint: 'Fattura elettronica inviata: da incassare' },
  paid: { label: 'Pagata', tone: 'good', hint: 'Già incassate' },
};

const dateIt = (d: string | null) => (d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('it-IT') : '—');

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function num(v: string): number {
  const n = Number(v.trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Fatture: chi emette le prepara (anche da un preventivo accettato); il cliente vede quelle emesse a suo nome. */
export function InvoicesPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const role = ctx.me.user.role;
  const isClient = role === 'client';
  const base = isClient ? '/api/client/invoices' : '/api/invoices';
  const [invoices, setInvoices] = useState<InvoiceDto[] | null>(null);
  const [filter, setFilter] = useState<'all' | InvoiceStatus>('all');
  const [openId, setOpenId] = useNavState<number | null>(ctx, 'invoice', (ctx.pageParam as { open?: number } | null)?.open ?? null);
  const [editing, setEditing] = useState<InvoiceDto | 'new' | null>(null);
  const [profile, setProfile] = useState<IssuerProfileDto | null | undefined>(undefined);
  const [showProfile, setShowProfile] = useState(!!(ctx.pageParam as { profile?: boolean } | null)?.profile);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInvoices(await api.get<InvoiceDto[]>(base));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, base]);

  useEffect(() => {
    void load();
    if (!isClient) api.get<{ profile: IssuerProfileDto | null }>('/api/invoices/profile').then((r) => setProfile(r.profile)).catch(() => setProfile(null));
  }, [load, api, isClient]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: invoices?.length ?? 0 };
    for (const i of invoices ?? []) c[i.status] = (c[i.status] ?? 0) + 1;
    return c;
  }, [invoices]);

  const visible = (invoices ?? []).filter((i) => filter === 'all' || i.status === filter);
  const open = invoices?.find((i) => i.id === openId) ?? null;
  const tabs: ('all' | InvoiceStatus)[] = isClient ? ['all', 'issued', 'sent', 'paid'] : ['all', 'draft', 'issued', 'sent', 'paid'];
  const toCollect = (invoices ?? []).filter((i) => i.status === 'issued' || i.status === 'sent').reduce((n, i) => n + i.totalCents, 0);
  const paid = (invoices ?? []).filter((i) => i.status === 'paid').reduce((n, i) => n + i.totalCents, 0);

  return (
    <div className="panels single">
      <Panel
        title="Fatture"
        subtitle={invoices ? `Da incassare ${euro(toCollect)} · incassate ${euro(paid)}` : undefined}
        right={
          !isClient && (
            <div className="row-actions" style={{ margin: 0 }}>
              <button type="button" className="btn" title="I tuoi dati che compaiono sulle fatture: P.IVA, indirizzo, IBAN" onClick={() => setShowProfile(true)}>
                <Icon name="gear" size={15} /> Dati fatturazione
              </button>
              <button type="button" className="btn dark" title="Prepara una fattura: resta bozza finché non la emetti" onClick={() => setEditing('new')}>
                <Icon name="plus" size={16} /> Nuova fattura
              </button>
            </div>
          )
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!isClient && profile === null && (
          <div className="mail-note" style={{ marginBottom: 12 }}>
            Prima di emettere fatture inserisci i tuoi <b>dati di fatturazione</b> (P.IVA, indirizzo, regime).{' '}
            <button type="button" className="btn small" onClick={() => setShowProfile(true)}>
              Compila ora
            </button>
          </div>
        )}
        <div className="quote-tabs">
          {tabs.map((t) => (
            <button key={t} type="button" className={`quote-tab${filter === t ? ' active' : ''}`} title={t === 'all' ? 'Tutte le fatture' : STATUS[t].hint} onClick={() => setFilter(t)}>
              {t === 'all' ? 'Tutte' : STATUS[t].label}
              <b>{counts[t] ?? 0}</b>
            </button>
          ))}
        </div>
        {!invoices ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="note">{isClient ? 'Nessuna fattura.' : 'Nessuna fattura. Creane una o parti da un preventivo accettato.'}</Empty>
        ) : (
          <div className="quote-list">
            {visible.map((i) => (
              <button key={i.id} type="button" className="quote-row" onClick={() => setOpenId(i.id)}>
                <div className="quote-main">
                  <div className="quote-num">{i.number ?? 'Bozza'}</div>
                  <div className="quote-subj">{i.subject}</div>
                  <div className="muted small">
                    {i.recipientName} · {dateIt(i.issueDate ?? i.createdAt)}
                    {role === 'admin' ? ` · ${i.createdByName}` : ''}
                  </div>
                </div>
                <div className="quote-side">
                  <b>{euro(i.totalCents)}</b>
                  <Pill tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Pill>
                </div>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {open && (
        <InvoiceView
          ctx={ctx}
          invoice={open}
          base={base}
          onClose={() => setOpenId(null)}
          onChanged={(inv) => setInvoices((l) => (l ?? []).map((x) => (x.id === inv.id ? inv : x)))}
          onEdit={() => {
            setEditing(open);
            setOpenId(null);
          }}
          onGone={() => {
            setOpenId(null);
            void load();
          }}
        />
      )}
      {editing && (
        <InvoiceEditor
          ctx={ctx}
          invoice={editing === 'new' ? null : editing}
          regime={profile?.regime ?? 'RF01'}
          onClose={() => setEditing(null)}
          onSaved={(inv) => {
            setEditing(null);
            void load().then(() => setOpenId(inv.id));
          }}
        />
      )}
      {showProfile && profile !== undefined && (
        <ProfileModal
          ctx={ctx}
          profile={profile ?? null}
          onClose={() => setShowProfile(false)}
          onSaved={(p) => {
            setProfile(p);
            setShowProfile(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function InvoiceView({
  ctx,
  invoice: inv,
  base,
  onClose,
  onChanged,
  onEdit,
  onGone,
}: {
  ctx: Ctx;
  invoice: InvoiceDto;
  base: string;
  onClose: () => void;
  onChanged: (i: InvoiceDto) => void;
  onEdit: () => void;
  onGone: () => void;
}) {
  const { api } = ctx;
  const role = ctx.me.user.role;
  const isClient = role === 'client';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mailing, setMailing] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const file = (ext: 'pdf' | 'xml') =>
    run(async () => saveBlob(await api.download(`${base}/${inv.id}/${ext}`), `${inv.number ?? 'bozza'}.${ext}`));
  const issue = () =>
    run(async () => {
      if (!(await ask('Emettere la fattura? Riceverà il numero progressivo e non sarà più modificabile.'))) return;
      onChanged(await api.post<InvoiceDto>(`/api/invoices/${inv.id}/issue`));
    });
  const status = (s: 'issued' | 'sent' | 'paid') => run(async () => onChanged(await api.post<InvoiceDto>(`/api/invoices/${inv.id}/status`, { status: s })));
  const remove = () =>
    run(async () => {
      if (!(await ask('Eliminare questa bozza?'))) return;
      await api.del(`/api/invoices/${inv.id}`);
      onGone();
    });

  return (
    <Modal
      title={inv.number ? `Fattura ${inv.number}` : 'Bozza di fattura'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={() => void file('pdf')} disabled={busy} title="Scarica la fattura in PDF">
            <Icon name="download" size={15} /> PDF
          </button>
          {!isClient && inv.status !== 'draft' && (
            <button type="button" className="btn" onClick={() => void file('xml')} disabled={busy} title="Il file della fattura elettronica, da caricare su Aruba">
              <Icon name="download" size={15} /> XML
            </button>
          )}
          {!isClient && inv.status !== 'draft' && (
            <button type="button" className="btn" onClick={() => setMailing(true)} disabled={busy} title="Manda al cliente una copia in PDF (quella valida resta la fattura elettronica)">
              <Icon name="send" size={15} /> Copia per email
            </button>
          )}
          {!isClient && inv.status === 'draft' && (
            <>
              <button type="button" className="btn ghost" onClick={() => void remove()} disabled={busy} title="Elimina bozza">
                <Icon name="trash" size={15} />
              </button>
              <button type="button" className="btn" onClick={onEdit} disabled={busy}>
                <Icon name="edit" size={15} /> Modifica
              </button>
              <button type="button" className="btn dark" onClick={() => void issue()} disabled={busy} title="Le dà il numero: dopo non si può più cambiare">
                <Icon name="check" size={15} /> Emetti
              </button>
            </>
          )}
          {!isClient && inv.status === 'issued' && (
            <button type="button" className="btn" onClick={() => void status('sent')} disabled={busy} title="L’hai caricata su Aruba ed è partita. SdI = Sistema di Interscambio: il canale dell’Agenzia delle Entrate per le fatture elettroniche">
              Segna inviata allo SdI
            </button>
          )}
          {!isClient && (inv.status === 'issued' || inv.status === 'sent') && (
            <button type="button" className="btn good" onClick={() => void status('paid')} disabled={busy} title="Il cliente ha pagato">
              Segna pagata
            </button>
          )}
          {!isClient && (inv.status === 'sent' || inv.status === 'paid') && (
            <button type="button" className="btn ghost" onClick={() => void status('issued')} disabled={busy} title="Torna indietro di un passo, se hai sbagliato">
              Riporta a emessa
            </button>
          )}
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="quote-doc">
        <div className="quote-doc-head">
          <div>
            <div className="quote-subj big">{inv.subject}</div>
            <div className="muted small">
              {inv.number ?? 'Bozza'} · {dateIt(inv.issueDate ?? inv.createdAt)}
              {inv.dueDate ? ` · scadenza ${dateIt(inv.dueDate)}` : ''}
            </div>
          </div>
          <Pill tone={STATUS[inv.status].tone}>{STATUS[inv.status].label}</Pill>
        </div>
        <div className="quote-to">
          <b>{inv.recipientName}</b>
          {inv.recipientVat && <div className="muted small">P.IVA: {inv.recipientVat}</div>}
          {inv.recipientCf && <div className="muted small">C.F.: {inv.recipientCf}</div>}
          {inv.recipientAddress && (
            <div className="muted small">
              {inv.recipientAddress}, {inv.recipientCap} {inv.recipientCity} {inv.recipientProvince ? `(${inv.recipientProvince})` : ''}
            </div>
          )}
          <div className="muted small">
            Codice destinatario: {inv.recipientSdi}
            {inv.recipientPec ? ` · PEC ${inv.recipientPec}` : ''}
          </div>
        </div>
        <table className="table quote-items">
          <thead>
            <tr>
              <th>Descrizione</th>
              <th style={{ textAlign: 'right' }}>Q.tà</th>
              <th style={{ textAlign: 'right' }}>Prezzo</th>
              <th style={{ textAlign: 'right' }}>Importo</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((i, k) => (
              <tr key={k}>
                <td>{i.description}</td>
                <td style={{ textAlign: 'right' }}>
                  {i.quantity.toLocaleString('it-IT')}
                  {i.kind === 'hours' ? ' h' : ''}
                </td>
                <td style={{ textAlign: 'right' }}>{euro(i.unitCents)}</td>
                <td style={{ textAlign: 'right' }}>{euro(i.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="quote-totals">
          <div>
            <span>Imponibile</span>
            <span>{euro(inv.subtotalCents)}</span>
          </div>
          {inv.discountCents > 0 && (
            <div>
              <span>Sconto</span>
              <span>− {euro(inv.discountCents)}</span>
            </div>
          )}
          <div>
            <span>{inv.vatPct > 0 ? `IVA ${inv.vatPct.toLocaleString('it-IT')}%` : 'IVA'}</span>
            <span>{inv.vatPct > 0 ? euro(inv.vatCents) : 'non applicata'}</span>
          </div>
          {inv.stampCents > 0 && (
            <div>
              <span>Bollo</span>
              <span>{euro(inv.stampCents)}</span>
            </div>
          )}
          <div className="grand">
            <span>Totale</span>
            <span>{euro(inv.totalCents)}</span>
          </div>
        </div>
        {inv.notes && <p className="quote-text">{inv.notes}</p>}
        {inv.status === 'paid' && <div className="mail-note">Pagata il {dateIt(inv.paidAt)}</div>}
        {!isClient && inv.status === 'issued' && (
          <div className="mail-note" title="Va bene anche un altro intermediario al posto di Aruba. SdI = Sistema di Interscambio: il canale dell’Agenzia delle Entrate per le fatture elettroniche.">
            Scarica l’<b>XML</b>, caricalo su Aruba, poi premi «Segna inviata allo SdI».
          </div>
        )}
      </div>
      {mailing && (
        <CopyMailModal
          ctx={ctx}
          invoice={inv}
          onClose={() => setMailing(false)}
        />
      )}
    </Modal>
  );
}

function CopyMailModal({ ctx, invoice: inv, onClose }: { ctx: Ctx; invoice: InvoiceDto; onClose: () => void }) {
  const [to, setTo] = useState(inv.recipientEmail ?? '');
  const [subject, setSubject] = useState(`Fattura ${inv.number} - ${inv.subject}`);
  const [text, setText] = useState(`Buongiorno,\n\nin allegato la copia di cortesia della fattura ${inv.number}. La fattura elettronica viene trasmessa tramite il Sistema di Interscambio.\n\nCordiali saluti,\n${ctx.me.user.name}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [when, setWhen] = useState<When | null>(null);

  const send = async () => {
    const list = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!list.length) return setError('Inserisci il destinatario');
    if (when) {
      const bad = whenError(when);
      if (bad) return setError(bad);
    }
    setBusy(true);
    setError(null);
    try {
      const sendAt = whenToIso(when) ?? undefined;
      await ctx.api.post(`/api/invoices/${inv.id}/send-copy`, { to: list, subject, text, sendAt });
      setDone(sendAt ? `Partirà ${whenLabel(sendAt)}: la trovi in Posta → Programmate.` : 'Email inviata.');
    } catch (e) {
      const msg = (e as Error).message;
      setError(/non configurata/i.test(msg) ? 'Collega prima la tua casella nella sezione Posta.' : msg);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Copia di cortesia"
      onClose={onClose}
      footer={
        done ? (
          <button type="button" className="btn dark" onClick={onClose}>
            Chiudi
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Annulla
            </button>
            {!when && (
              <button type="button" className="btn" onClick={() => setWhen(newWhen())} disabled={busy} title="Scegli giorno e ora dell'invio">
                <Icon name="clock" size={15} /> Programma
              </button>
            )}
            <button type="button" className="btn dark" onClick={() => void send()} disabled={busy}>
              <Icon name={when ? 'clock' : 'send'} size={15} /> {busy ? (when ? 'Salvataggio…' : 'Invio…') : when ? 'Programma invio' : 'Invia con PDF'}
            </button>
          </>
        )
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {done ? (
        <div className="mail-note">{done}</div>
      ) : (
        <>
          <Field label="A">
            <input value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Oggetto">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Messaggio">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} style={{ minHeight: 160 }} />
          </Field>
          {when && <SchedulePicker value={when} onChange={setWhen} onClear={() => setWhen(null)} />}
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function ProfileModal({ ctx, profile, onClose, onSaved }: { ctx: Ctx; profile: IssuerProfileDto | null; onClose: () => void; onSaved: (p: IssuerProfileDto) => void }) {
  const [p, setP] = useState<IssuerProfileDto>(
    profile ?? { name: '', vat: '', cf: '', regime: 'RF01', address: '', cap: '', city: '', province: '', email: ctx.me.user.email, pec: '', phone: '', iban: '' },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof IssuerProfileDto>(k: K, v: IssuerProfileDto[K]) => setP((x) => ({ ...x, [k]: v }));
  const text = (k: 'name' | 'vat' | 'cf' | 'address' | 'cap' | 'city' | 'province' | 'email' | 'pec' | 'phone' | 'iban') => ({
    value: p[k] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(k, e.target.value as never),
  });

  const save = async () => {
    if (!p.name.trim()) return setError('Indica la ragione sociale o il tuo nome');
    setBusy(true);
    setError(null);
    try {
      const r = await ctx.api.put<{ profile: IssuerProfileDto }>('/api/invoices/profile', { ...p, vat: p.vat || null, cf: p.cf || null, email: p.email || null, pec: p.pec || null, phone: p.phone || null, iban: p.iban || null });
      onSaved(r.profile);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Dati di fatturazione"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <p className="muted small">I dati di chi emette la fattura (compaiono su PDF e XML). Ognuno usa i propri.</p>
      <div className="fields-2">
        <Field label="Ragione sociale / nome e cognome">
          <input {...text('name')} />
        </Field>
        <Field label="Regime fiscale">
          <select value={p.regime} onChange={(e) => set('regime', e.target.value as 'RF01' | 'RF19')}>
            <option value="RF01">Ordinario (con IVA)</option>
            <option value="RF19">Forfettario (senza IVA)</option>
          </select>
        </Field>
        <Field label="Partita IVA">
          <input {...text('vat')} />
        </Field>
        <Field label="Codice fiscale">
          <input {...text('cf')} />
        </Field>
        <Field label="Indirizzo">
          <input {...text('address')} />
        </Field>
        <Field label="CAP">
          <input {...text('cap')} />
        </Field>
        <Field label="Comune">
          <input {...text('city')} />
        </Field>
        <Field label="Provincia (sigla)">
          <input {...text('province')} maxLength={2} />
        </Field>
        <Field label="Email">
          <input {...text('email')} />
        </Field>
        <Field label="Telefono">
          <input {...text('phone')} />
        </Field>
        <Field label="PEC">
          <input {...text('pec')} />
        </Field>
        <Field label="IBAN per i pagamenti">
          <input {...text('iban')} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

interface ItemDraft {
  description: string;
  kind: QuoteItemKind;
  quantity: string;
  unit: string;
}

function InvoiceEditor({ ctx, invoice, regime, onClose, onSaved }: { ctx: Ctx; invoice: InvoiceDto | null; regime: 'RF01' | 'RF19'; onClose: () => void; onSaved: (i: InvoiceDto) => void }) {
  const { api } = ctx;
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [clientId, setClientId] = useState<number | null>(invoice?.clientId ?? null);
  const [name, setName] = useState(invoice?.recipientName ?? '');
  const [vat, setVat] = useState(invoice?.recipientVat ?? '');
  const [cf, setCf] = useState(invoice?.recipientCf ?? '');
  const [address, setAddress] = useState(invoice?.recipientAddress ?? '');
  const [cap, setCap] = useState(invoice?.recipientCap ?? '');
  const [city, setCity] = useState(invoice?.recipientCity ?? '');
  const [prov, setProv] = useState(invoice?.recipientProvince ?? '');
  const [email, setEmail] = useState(invoice?.recipientEmail ?? '');
  const [pec, setPec] = useState(invoice?.recipientPec ?? '');
  const [sdi, setSdi] = useState(invoice?.recipientSdi ?? '0000000');
  const [subject, setSubject] = useState(invoice?.subject ?? '');
  const [notes, setNotes] = useState(invoice?.notes ?? '');
  const [vatPct, setVatPct] = useState(invoice ? String(invoice.vatPct).replace('.', ',') : regime === 'RF19' ? '0' : '22');
  const [discount, setDiscount] = useState(invoice && invoice.discountCents ? euroInput(invoice.discountCents) : '');
  const [stamp, setStamp] = useState(invoice ? invoice.stampCents > 0 : false);
  const [issueDate, setIssueDate] = useState(invoice?.issueDate ?? todayKey());
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? '');
  const [method, setMethod] = useState(invoice?.paymentMethod ?? 'MP05');
  const [items, setItems] = useState<ItemDraft[]>(
    invoice
      ? invoice.items.map((i) => ({ description: i.description, kind: i.kind, quantity: String(i.quantity).replace('.', ','), unit: euroInput(i.unitCents) }))
      : [{ description: '', kind: 'fixed', quantity: '1', unit: '' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: number; name: string }[]>('/api/invoices/meta/clients').then(setClients).catch(() => undefined);
  }, [api]);

  const parsed = items.map((i) => ({ quantity: num(i.quantity), unitCents: parseEuro(i.unit) ?? 0 }));
  const subtotal = parsed.reduce((n, i) => n + Math.round(i.quantity * i.unitCents), 0);
  const taxable = Math.max(0, subtotal - (parseEuro(discount) ?? 0));
  const vatCents = Math.round((taxable * num(vatPct)) / 100);
  const stampCents = stamp ? 200 : 0;
  const total = taxable + vatCents + stampCents;
  const stampAdvice = num(vatPct) === 0 && taxable > 7747;

  const setItem = (idx: number, patch: Partial<ItemDraft>) => setItems((l) => l.map((x, k) => (k === idx ? { ...x, ...patch } : x)));

  const save = async () => {
    if (!name.trim()) return setError('Indica l’intestatario');
    if (!subject.trim()) return setError('Indica l’oggetto');
    if (items.some((i) => !i.description.trim())) return setError('Ogni riga deve avere una descrizione');
    setBusy(true);
    setError(null);
    const body = {
      clientId,
      quoteId: invoice?.quoteId ?? null,
      recipientName: name.trim(),
      recipientVat: vat.trim() || null,
      recipientCf: cf.trim() || null,
      recipientAddress: address.trim() || null,
      recipientCap: cap.trim() || null,
      recipientCity: city.trim() || null,
      recipientProvince: prov.trim() || null,
      recipientEmail: email.trim() || null,
      recipientPec: pec.trim() || null,
      recipientSdi: (sdi.trim() || '0000000').toUpperCase(),
      subject: subject.trim(),
      notes: notes.trim() || null,
      vatPct: num(vatPct),
      discountCents: parseEuro(discount) ?? 0,
      stampCents,
      issueDate: issueDate || null,
      dueDate: dueDate || null,
      paymentMethod: method,
      items: items.map((i, k) => ({ description: i.description.trim(), kind: i.kind, quantity: parsed[k].quantity, unitCents: parsed[k].unitCents })),
    };
    try {
      onSaved(invoice ? await api.put<InvoiceDto>(`/api/invoices/${invoice.id}`, body) : await api.post<InvoiceDto>('/api/invoices', body));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={invoice ? 'Modifica bozza' : 'Nuova fattura'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva bozza'}
          </button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {clients.length > 0 && (
        <Field label="Cliente esistente" hint="Se lo colleghi, il cliente vedrà la fattura nella sua area.">
          <select
            value={clientId ?? ''}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              setClientId(id);
              const c = clients.find((x) => x.id === id);
              if (c && !name.trim()) setName(c.name);
            }}
          >
            <option value="">— Non collegato —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <div className="fields-2">
        <Field label="Intestatario">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Partita IVA">
          <input value={vat} onChange={(e) => setVat(e.target.value)} />
        </Field>
        <Field label="Codice fiscale">
          <input value={cf} onChange={(e) => setCf(e.target.value)} />
        </Field>
        <Field label="Indirizzo">
          <input value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label="CAP">
          <input value={cap} onChange={(e) => setCap(e.target.value)} />
        </Field>
        <Field label="Comune">
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="Provincia (sigla)">
          <input value={prov} onChange={(e) => setProv(e.target.value)} maxLength={2} />
        </Field>
        <Field label="Email">
          <input value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Codice destinatario (SdI)" hint="7 caratteri, lo dà il cliente; 0000000 se usa la PEC">
          <input value={sdi} onChange={(e) => setSdi(e.target.value)} maxLength={7} />
        </Field>
        <Field label="PEC del cliente">
          <input value={pec} onChange={(e) => setPec(e.target.value)} />
        </Field>
      </div>
      <Field label="Oggetto">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Es. Consulenza informatica settembre" />
      </Field>

      <div className="quote-edit-items">
        <div className="muted small" style={{ marginBottom: 6 }}>
          Righe
        </div>
        {items.map((it, idx) => (
          <div key={idx} className="quote-edit-row">
            <input className="qe-desc" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} placeholder="Descrizione" />
            <select className="qe-kind" value={it.kind} onChange={(e) => setItem(idx, { kind: e.target.value as QuoteItemKind })}>
              <option value="fixed">A corpo</option>
              <option value="hours">Ore</option>
            </select>
            <input className="qe-num" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} inputMode="decimal" title="Quantità" />
            <input className="qe-num" value={it.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} inputMode="decimal" placeholder="€" title="Prezzo unitario" />
            <span className="qe-tot">{euro(Math.round(parsed[idx].quantity * parsed[idx].unitCents))}</span>
            <button type="button" className="btn icon small ghost" disabled={items.length === 1} title="Togli la riga" onClick={() => setItems((l) => l.filter((_, k) => k !== idx))}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button type="button" className="btn small" onClick={() => setItems((l) => [...l, { description: '', kind: 'fixed', quantity: '1', unit: '' }])}>
          <Icon name="plus" size={14} /> Aggiungi riga
        </button>
      </div>

      <div className="fields-2" style={{ marginTop: 14 }}>
        <Field label="Sconto (€)">
          <input value={discount} onChange={(e) => setDiscount(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="IVA (%)" hint={regime === 'RF19' ? 'Forfettario: 0' : undefined}>
          <input value={vatPct} onChange={(e) => setVatPct(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Data fattura">
          <DateInput value={issueDate} onChange={setIssueDate} />
        </Field>
        <Field label="Scadenza pagamento">
          <DateInput value={dueDate} onChange={setDueDate} allowEmpty />
        </Field>
        <Field label="Modalità di pagamento">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="MP05">Bonifico</option>
            <option value="MP01">Contanti</option>
            <option value="MP08">Carta di pagamento</option>
            <option value="MP12">Ricevuta bancaria (RIBA)</option>
          </select>
        </Field>
        <Field label="Marca da bollo virtuale (2 €)" hint={stampAdvice ? 'Consigliata: senza IVA e importo oltre 77,47 €' : undefined}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} /> Addebita bollo
          </label>
        </Field>
      </div>
      <Field label="Note (facoltative)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </Field>
      <div className="quote-totals">
        <div>
          <span>Imponibile</span>
          <span>{euro(taxable)}</span>
        </div>
        <div>
          <span>IVA</span>
          <span>{euro(vatCents)}</span>
        </div>
        {stamp && (
          <div>
            <span>Bollo</span>
            <span>{euro(200)}</span>
          </div>
        )}
        <div className="grand">
          <span>Totale</span>
          <span>{euro(total)}</span>
        </div>
      </div>
    </Modal>
  );
}
